import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import MentionTextarea, { renderMentionText } from '../components/MentionTextarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  FileText,
  Send,
  Check,
  X,
  Edit,
  History,
  MessageSquare,
  User,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Calculator,
  GitBranch,
  Eye,
  Archive,
  Filter,
  Calendar,
  ExternalLink,
  Loader2,
  TrendingDown,
  TrendingUp
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { CURRENCY_SYMBOL_MAP } from '../constants/currencies';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtPrice = (val, currency = 'INR') => {
  if (val == null) return '-';
  const symbol = CURRENCY_SYMBOL_MAP[currency] ?? currency;
  return `${symbol}${Number(val).toLocaleString()} ${currency}`;
};
const fmtPct = (val, d = 1) => (val != null ? `${Number(val).toFixed(d)}%` : '-');

const PriceHistoryTooltip = ({ active, payload, label, isDark, currency = 'INR' }) => {
  if (active && payload && payload.length) {
    const symbol = CURRENCY_SYMBOL_MAP[currency] ?? currency;
    return (
      <div className={`${isDark ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-zinc-200'} border p-3 rounded shadow-xl`}>
        <p className={`${isDark ? 'text-white' : 'text-zinc-900'} font-medium mb-1`}>{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {typeof entry.value === 'number' ? `${symbol}${entry.value.toLocaleString()} ${currency}` : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const StatusBadge = ({ status, large = false }) => {
  const styles = {
    draft: 'bg-zinc-200 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300',
    pending: 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30',
    in_review: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-500/30',
    approved: 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/30',
    rejected: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-500/30',
    on_hold: 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-500/30',
    needs_info: 'bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-400 border border-purple-200 dark:border-purple-500/30'
  };

  const labels = {
    draft: 'Draft',
    pending: 'Pending',
    in_review: 'In Review',
    approved: 'Approved',
    rejected: 'Rejected',
    on_hold: 'On Hold',
    needs_info: 'Needs Info'
  };

  return (
    <span className={`${large ? 'px-4 py-2 text-sm' : 'px-2 py-1 text-xs'} rounded ${styles[status] || styles.draft}`}>
      {labels[status] || status}
    </span>
  );
};

// Editable COGS Card View
const EditableCOGSCard = ({
  data,
  isHistorical = false,
  isOldVersion = false, // Whether viewing old version from History tab
  editMode = false,
  editedData,
  setEditedData,
  onCancel,
  onSave,
  onSaveAndApprove,
  saving = false,
  isApprover = false,
  snapshotInfo = null // Info about the snapshot being viewed
}) => {
  if (!data) return null;

  // Normalize data to map new backend field names to display field names
  const normalizeData = (rawData) => {
    if (!rawData) return rawData;
    return {
      ...rawData,
      // Map new backend fields to display fields
      sap_purchase_price: rawData.purchase_price || rawData.sap_purchase_price || rawData.purchase_price_inr || 0,
      total_cost: rawData.cogs || rawData.total_cost || 0,
      dealer_price: rawData.dbp || rawData.dealer_price || 0,
      dealer_markup: rawData.gp_amount || rawData.dealer_markup || 0,
      mrp_markup: rawData.dealer_margin || rawData.ddr_amount || rawData.mrp_markup || 0,
    };
  };

  // Use editedData if in edit mode and it exists, otherwise use original data
  const rawDisplayData = (editMode && editedData) ? editedData : data;
  const displayData = normalizeData(rawDisplayData);

  // If editMode is on but editedData isn't ready yet, show loading or original data
  if (editMode && !editedData) {
    return null;
  }

  // Helper function to format currency
  const formatCurrency = (amount) => {
    const currency = displayData?.currency || 'INR';
    const symbols = { INR: '₹', USD: '$', EUR: '€', JPY: '¥', CNY: '¥' };
    return `${symbols[currency] || currency}${parseFloat(amount || 0).toLocaleString()}`;
  };

  // Convert cost_breakdown key to readable label
  const formatLabel = (key) => {
    return key.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  // Handle field change
  const handleFieldChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  // Handle cost breakdown change
  const handleCostBreakdownChange = (key, value) => {
    setEditedData(prev => ({
      ...prev,
      cost_breakdown: {
        ...prev.cost_breakdown,
        [key]: parseFloat(value) || 0
      }
    }));
  };

  // Recalculate totals when values change
  const recalculateTotals = () => {
    if (!editedData) return;

    // Get purchase price (may have been edited)
    const purchasePrice = parseFloat(editedData.purchase_price) || parseFloat(editedData.purchase_price_inr) || parseFloat(editedData.sap_purchase_price) || 0;

    // Get ISC percent and calculate ISC amount
    const iscPercent = parseFloat(editedData.isc_percent) || 12.5;
    const iscAmount = purchasePrice * (iscPercent / 100);

    // Get customs duty
    const customsDutyPercent = parseFloat(editedData.customs_duty_percent) || 0;
    const customsDutyAmount = purchasePrice * (customsDutyPercent / 100);

    // Calculate COGS = PP + ISC + Customs
    const cogs = purchasePrice + iscAmount + customsDutyAmount;

    // GP calculation: DBP = COGS / (1 - GP%)
    const gpPercent = parseFloat(editedData.gp_percent) || 50;
    if (gpPercent >= 100) {
      console.error('GP% must be less than 100%');
      return;
    }
    const dbp = cogs / (1 - gpPercent / 100);
    const gpAmount = dbp - cogs;

    // DDR calculation: MRP = DBP / (1 - DDR%)
    const ddrPercent = parseFloat(editedData.ddr_percent) || 35;
    if (ddrPercent >= 100) {
      console.error('DDR% must be less than 100%');
      return;
    }
    const mrp = dbp / (1 - ddrPercent / 100);

    // CBP = MRP / 1.18 (MRP is inclusive of 18% GST)
    const cbp = mrp / 1.18;

    // Dealer Margin = CBP - DBP
    const dealerMargin = cbp - dbp;
    const dealerMarginPercent = mrp > 0 ? (dealerMargin / mrp) * 100 : 0;

    setEditedData(prev => ({
      ...prev,
      purchase_price: purchasePrice,
      purchase_price_inr: purchasePrice,
      isc_percent: iscPercent,
      isc_amount: parseFloat(iscAmount.toFixed(2)),
      customs_duty_percent: customsDutyPercent,
      cogs: parseFloat(cogs.toFixed(2)),
      gp_percent: gpPercent,
      gp_amount: parseFloat(gpAmount.toFixed(2)),
      dbp: parseFloat(dbp.toFixed(2)),
      dealer_price: parseFloat(dbp.toFixed(2)),
      ddr_percent: ddrPercent,
      mrp: parseFloat(mrp.toFixed(2)),
      cbp: parseFloat(cbp.toFixed(2)),
      dealer_margin: parseFloat(dealerMargin.toFixed(2)),
      dealer_margin_percent: parseFloat(dealerMarginPercent.toFixed(2))
    }));
  };

  return (
    <div className={`space-y-4 ${isHistorical ? 'opacity-90' : ''}`}>
      {/* Detailed Snapshot Metadata Panel - Show when viewing Activity tab snapshots */}
      {isHistorical && snapshotInfo && (
        <div className="p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <History className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 font-medium text-sm uppercase tracking-wider">Snapshot Details</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Action/Status */}
            <div className="space-y-1">
              <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Action</p>
              <p className={`text-sm font-medium capitalize ${snapshotInfo.action === 'approved' ? 'text-emerald-400' :
                snapshotInfo.action === 'rejected' ? 'text-red-400' :
                  snapshotInfo.action === 'revised' ? 'text-amber-400' :
                    snapshotInfo.action === 'updated' ? 'text-blue-400' :
                      snapshotInfo.action === 'comment' ? 'text-purple-400' :
                        snapshotInfo.action === 'in_review' ? 'text-indigo-400' :
                          snapshotInfo.action === 'on_hold' ? 'text-orange-400' :
                            snapshotInfo.action === 'needs_info' ? 'text-yellow-400' :
                              'text-cyan-400'
                }`}>
                {snapshotInfo.action?.replace('_', ' ') || 'Unknown'}
              </p>
            </div>
            {/* Performed By */}
            <div className="space-y-1">
              <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Performed By</p>
              <p className="text-sm text-zinc-800 dark:text-zinc-300 font-medium">{snapshotInfo.performed_by_name || 'Unknown'}</p>
            </div>
            {/* Timestamp */}
            <div className="space-y-1">
              <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Timestamp</p>
              <p className="text-sm text-zinc-800 dark:text-zinc-300 font-medium">
                {snapshotInfo.created_at ? new Date(snapshotInfo.created_at).toLocaleString() : 'Unknown'}
              </p>
            </div>
            {/* Status at time */}
            <div className="space-y-1">
              <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Card Status</p>
              <p className="text-sm text-zinc-800 dark:text-zinc-300 font-medium capitalize">{data?.status?.replace('_', ' ') || 'Unknown'}</p>
            </div>
          </div>
          {/* Comments if available */}
          {snapshotInfo.comments && (
            <div className="mt-3 pt-3 border-t border-amber-500/20">
              <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-1">Remarks</p>
              <p className="text-sm text-zinc-700 dark:text-zinc-300 bg-amber-100/50 dark:bg-black/20 p-2 rounded">{snapshotInfo.comments}</p>
            </div>
          )}
          {/* Additional Metadata */}
          <div className="mt-3 pt-3 border-t border-amber-500/20 grid grid-cols-3 gap-4">
            <div className="space-y-1">
              <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Part Number</p>
              <p className="text-sm font-mono text-zinc-800 dark:text-zinc-300 font-medium">{data?.part_number || '-'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Dealer Price</p>
              <p className="text-sm font-mono text-emerald-600 dark:text-emerald-400 font-semibold">₹{data?.dealer_price?.toLocaleString() || '0'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">MRP</p>
              <p className="text-sm font-mono text-amber-600 dark:text-amber-400 font-semibold">₹{data?.mrp?.toLocaleString() || '0'}</p>
            </div>
          </div>
        </div>
      )}

      {editMode && (
        <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded text-sm text-blue-400 flex items-center gap-2">
          <Edit className="w-4 h-4" />
          Edit mode active — modify values, recalculate, then save
        </div>
      )}

      {/* Part Info - Compact Header */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 p-3 bg-zinc-100 dark:bg-zinc-900 rounded border border-zinc-200 dark:border-zinc-800">
        <div>
          <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Part Number</p>
          <p className="font-mono text-zinc-900 dark:text-white">{displayData.part_number}</p>
        </div>
        <div>
          <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Part Name</p>
          <p className="text-zinc-700 dark:text-zinc-300 text-sm">{displayData.part_name}</p>
        </div>
        <div>
          <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Family</p>
          <p className="text-zinc-700 dark:text-zinc-300 text-sm">{displayData.family_name}</p>
        </div>
        <div>
          <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Template</p>
          <p className="text-zinc-700 dark:text-zinc-300 text-sm">{displayData.template_name}</p>
        </div>
      </div>

      {/* Vendor/Currency/Priority Row - Compact */}
      <div className="grid grid-cols-4 gap-3 p-3 bg-zinc-100 dark:bg-zinc-900 rounded border border-zinc-200 dark:border-zinc-800">
        <div>
          <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Vendor</p>
          <p className="font-mono text-zinc-700 dark:text-zinc-300 text-sm">{displayData.vendor_code || '-'}</p>
        </div>
        <div>
          <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Currency</p>
          <p className="text-zinc-700 dark:text-zinc-300 text-sm">{displayData.currency || 'INR'}</p>
        </div>
        <div>
          <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Priority</p>
          <span className={`text-xs px-2 py-0.5 rounded ${displayData.priority === 'high' ? 'bg-red-500/20 text-red-400' :
            displayData.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-green-500/20 text-green-400'
            }`}>
            {displayData.priority || 'medium'}
          </span>
        </div>
        <div>
          <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Due Date</p>
          <p className="text-zinc-700 dark:text-zinc-300 text-sm">{displayData.due_date ? new Date(displayData.due_date).toLocaleDateString() : '-'}</p>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Cost Breakdown */}
        <div className={`p-5 rounded border ${editMode ? 'bg-white dark:bg-zinc-900 border-blue-500/30' : 'bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800'}`}>
          <h4 className="text-sm font-medium text-zinc-600 dark:text-zinc-400 uppercase tracking-wider mb-4">
            Cost Breakdown {editMode && <span className="text-blue-400 normal-case font-normal">(editable)</span>}
          </h4>

          <div className="space-y-2">
            {/* SAP Purchase Price */}
            <div className={`flex items-center justify-between py-2 ${editMode ? 'bg-zinc-100 dark:bg-zinc-800/50 px-3 rounded' : ''}`}>
              <span className="text-zinc-600 dark:text-zinc-400 text-sm">SAP Purchase Price</span>

              <span className="font-mono text-zinc-900 dark:text-white">{formatCurrency(displayData.sap_purchase_price)}</span>
            </div>

            {/* ISC (Integrated Supply Cost) */}
            {(displayData.isc_percent !== undefined || displayData.isc_amount !== undefined) && (
              <div className={`flex items-center justify-between py-2 ${editMode ? 'bg-zinc-100 dark:bg-zinc-800/50 px-3 rounded' : ''}`}>
                <span className="text-zinc-600 dark:text-zinc-400 text-sm">ISC @ {displayData.isc_percent || 0}%</span>
                <span className="font-mono text-zinc-600 dark:text-zinc-400">+{formatCurrency(displayData.isc_amount)}</span>
              </div>
            )}

            {/* Customs Duty */}
            {(displayData.customs_duty_percent !== undefined || displayData.customs_amount !== undefined) && (
              <div className={`flex items-center justify-between py-2 ${editMode ? 'bg-zinc-100 dark:bg-zinc-800/50 px-3 rounded' : ''}`}>
                <span className="text-zinc-600 dark:text-zinc-400 text-sm">Customs Duty @ {displayData.customs_duty_percent || 0}%</span>
                <span className="font-mono text-zinc-600 dark:text-zinc-400">+{formatCurrency(displayData.customs_amount)}</span>
              </div>
            )}

            {/* Legacy cost breakdown fields (for backwards compatibility) */}
            {displayData.cost_breakdown && Object.entries(displayData.cost_breakdown).map(([key, value]) => (
              <div key={key} className={`flex items-center justify-between py-2 ${editMode ? 'bg-zinc-100 dark:bg-zinc-800/50 px-3 rounded' : ''}`}>
                <span className="text-zinc-600 dark:text-zinc-400 text-sm">{formatLabel(key)}</span>
                <span className="font-mono text-zinc-600 dark:text-zinc-400">+{formatCurrency(value)}</span>
              </div>
            ))}

            {/* COGS (Cost of Goods Sold) */}
            <div className="flex justify-between items-center pt-3 mt-2 border-t border-zinc-700">
              <span className="text-zinc-900 dark:text-white font-medium">COGS (Total Cost)</span>
              <span className="font-mono text-cyan-400 font-semibold text-lg">{formatCurrency(displayData.total_cost)}</span>
            </div>
          </div>
        </div>

        {/* Final Pricing */}
        <div className={`p-5 rounded border ${editMode ? 'bg-white dark:bg-zinc-900 border-blue-500/30' : 'bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800'}`}>
          <h4 className="text-sm font-medium text-zinc-600 dark:text-zinc-400 uppercase tracking-wider mb-4">
            Final Pricing {editMode && <span className="text-blue-400 normal-case font-normal">(editable)</span>}
          </h4>

          <div className="space-y-3">
            {/* GP% Row */}
            <div className={`py-2 ${editMode ? 'bg-zinc-100 dark:bg-zinc-800/50 px-3 rounded' : ''}`}>
              <div className="flex items-center justify-between">
                <span className="text-zinc-600 dark:text-zinc-400 text-sm">Gross Profit (GP%)</span>
                {editMode ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      step="0.1"
                      value={displayData.gp_percent || 0}
                      onChange={(e) => handleFieldChange('gp_percent', e.target.value)}
                      className="w-16 bg-white dark:bg-zinc-900 border border-zinc-600 rounded px-2 py-1.5 text-right font-mono text-emerald-400 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    />
                    <span className="text-zinc-500 text-sm">%</span>
                  </div>
                ) : (
                  <span className="font-mono text-emerald-400">{displayData.gp_percent}%</span>
                )}
              </div>
              {!editMode && (
                <p className="text-xs text-zinc-500 mt-1 text-right">+{formatCurrency(displayData.gp_amount || displayData.dealer_markup)}</p>
              )}
            </div>

            {/* Dealer Price */}
            <div className="flex justify-between p-3 bg-emerald-500/10 rounded border border-emerald-500/20">
              <span className="text-emerald-400 font-medium">Dealer Price</span>
              <span className="font-mono text-emerald-400 font-bold text-lg">{formatCurrency(displayData.dealer_price)}</span>
            </div>

            {/* DDR% Row */}
            <div className={`py-2 mt-2 ${editMode ? 'bg-zinc-100 dark:bg-zinc-800/50 px-3 rounded' : ''}`}>
              <div className="flex items-center justify-between">
                <span className="text-zinc-600 dark:text-zinc-400 text-sm">Dealer Margin (DDR%)</span>
                {editMode ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      step="0.1"
                      value={displayData.ddr_percent || 0}
                      onChange={(e) => handleFieldChange('ddr_percent', e.target.value)}
                      className="w-16 bg-white dark:bg-zinc-900 border border-zinc-600 rounded px-2 py-1.5 text-right font-mono text-amber-400 text-sm focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
                    />
                    <span className="text-zinc-500 text-sm">%</span>
                  </div>
                ) : (
                  <span className="font-mono text-amber-400">{displayData.ddr_percent}%</span>
                )}
              </div>
              {!editMode && (
                <p className="text-xs text-zinc-500 mt-1 text-right">+{formatCurrency(displayData.ddr_amount || displayData.mrp_markup)}</p>
              )}
            </div>

            {/* MRP */}
            <div className="flex justify-between p-3 bg-amber-500/10 rounded border border-amber-500/20">
              <span className="text-amber-400 font-medium">MRP</span>
              <span className="font-mono text-amber-400 font-bold text-lg">{formatCurrency(displayData.mrp)}</span>
              <span className="text-xs text-amber-500/70">Incl. GST</span>
            </div>

            {/* CBP (Cost Before GST) */}
            {displayData.cbp !== undefined && (
              <div className="flex justify-between p-3 bg-orange-500/10 rounded border border-orange-500/20 mt-2">
                <span className="text-orange-400 font-medium">CBP</span>
                <span className="font-mono text-orange-400 font-bold text-lg">{formatCurrency(displayData.cbp)}</span>
                <span className="text-xs text-orange-500/70">Excl. GST</span>
              </div>
            )}

            {/* Dealer Margin */}
            {(displayData.dealer_margin !== undefined || displayData.dealer_margin_percent !== undefined) && (
              <div className="flex justify-between items-center py-2 mt-2">
                <span className="text-zinc-600 dark:text-zinc-400 text-sm">Dealer Margin</span>
                <div className="text-right">
                  <span className="font-mono text-purple-400">{formatCurrency(displayData.dealer_margin)}</span>
                  <span className="text-xs text-zinc-500 ml-2">({displayData.dealer_margin_percent || 0}%)</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons - All together at bottom */}
      {editMode && (
        <div className="flex items-center justify-center gap-3 pt-4 pb-2 border-t border-zinc-200 dark:border-zinc-800 mt-4">
          <Button
            onClick={onCancel}
            variant="outline"
            className="border-zinc-600 hover:bg-zinc-100 dark:bg-zinc-800 px-6"
          >
            Cancel
          </Button>
          <Button
            onClick={recalculateTotals}
            variant="outline"
            className="border-blue-500 text-blue-400 hover:bg-blue-500/10 px-6"
          >
            <Calculator className="w-4 h-4 mr-2" />
            Recalculate
          </Button>

          {isApprover ? (
            /* Approver: Can save and directly approve */
            <>
              <Button
                onClick={onSave}
                disabled={saving}
                variant="outline"
                className="border-zinc-600 hover:bg-zinc-100 dark:bg-zinc-800 px-6"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                onClick={onSaveAndApprove}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 px-6"
              >
                {saving ? 'Saving...' : 'Save & Approve'}
              </Button>
            </>
          ) : (
            /* Creator: Submit for approval */
            <Button
              onClick={onSave}
              disabled={saving}
              className="bg-[#C3002F] hover:bg-[#9f0026] px-6"
            >
              {saving ? 'Saving...' : 'Save & Submit for Approval'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default function CostCardDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const { isDark } = useTheme();
  const commentsEndRef = useRef(null);

  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [viewingSnapshot, setViewingSnapshot] = useState(null);
  const [viewingSnapshotIndex, setViewingSnapshotIndex] = useState(null); // Track which activity item is being viewed
  const [viewingSnapshotInfo, setViewingSnapshotInfo] = useState(null); // Store full activity item info for metadata display
  const [snapshotUpdateKey, setSnapshotUpdateKey] = useState(0); // Force re-render when snapshot changes

  // Version history for the part
  const [versionHistory, setVersionHistory] = useState(null);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [sidebarTab, setSidebarTab] = useState('activity'); // 'activity' or 'versions'

  // Comment input
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  // Activity filters
  const [activityStatusFilter, setActivityStatusFilter] = useState('all');
  const [activityDateFilter, setActivityDateFilter] = useState('all');
  const [exactDate, setExactDate] = useState('');

  // Status change
  const [statusComment, setStatusComment] = useState('');
  const [changingStatus, setChangingStatus] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');

  useEffect(() => {
    if (card?.status) setSelectedStatus(card.status);
  }, [card?.status]);

  // Edit mode for inline editing
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [saving, setSaving] = useState(false);

  // Competitor pricing
  const [competitorPricing, setCompetitorPricing] = useState(null);
  const [competitorLoading, setCompetitorLoading] = useState(false);

  // Price history
  const [priceHistory, setPriceHistory] = useState(null);
  const [priceHistoryLoading, setPriceHistoryLoading] = useState(false);

  // Revision (legacy)
  const [revisionMode, setRevisionMode] = useState(false);
  const [revisionData, setRevisionData] = useState({
    dealer_markup_override: null,
    mrp_markup_override: null,
    comments: ''
  });

  useEffect(() => {
    fetchCard();
    fetchHistory();
  }, [id]);

  // Fetch version history when card loads
  useEffect(() => {
    if (card?.part_number) {
      fetchVersionHistory(card.part_number);
    }
  }, [card?.part_number]);

  // Fetch competitor pricing when card loads
  useEffect(() => {
    if (!card?.part_number) return;
    setCompetitorLoading(true);
    axios.get(`${API}/analytics/part/${encodeURIComponent(card.part_number)}/competitor-pricing`)
      .then(res => setCompetitorPricing(res.data))
      .catch(() => setCompetitorPricing(null))
      .finally(() => setCompetitorLoading(false));
  }, [card?.part_number]);

  // Fetch price history when card loads
  useEffect(() => {
    if (!card?.part_number) return;
    setPriceHistoryLoading(true);
    axios.get(`${API}/analytics/by-part-history?part_number=${encodeURIComponent(card.part_number)}`)
      .then(res => setPriceHistory(res.data))
      .catch(() => setPriceHistory(null))
      .finally(() => setPriceHistoryLoading(false));
  }, [card?.part_number]);

  // Initialize editedData when card loads or edit mode starts
  useEffect(() => {
    if (card && editMode) {
      setEditedData({ ...card });
    }
  }, [editMode, card]);

  const fetchCard = async () => {
    try {
      const response = await axios.get(`${API}/cogs-cards/${id}`);
      setCard(response.data);
    } catch (error) {
      toast.error('Failed to load COGS card');
      navigate('/cogs-cards');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/cost-cards/${id}/history`);
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
    }
  };

  const fetchVersionHistory = async (partNumber) => {
    setLoadingVersions(true);
    try {
      const response = await axios.get(`${API}/cogs-cards/history/part/${partNumber}`);
      setVersionHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch version history:', error);
    } finally {
      setLoadingVersions(false);
    }
  };

  // Save edited COGS card and resubmit for approval (for creators)
  const handleSaveAndResubmit = async () => {
    if (!editedData) return;

    setSaving(true);
    try {
      await axios.put(`${API}/cogs-cards/${id}`, {
        ...editedData,
        status: 'pending',
        comments: `Revised and resubmitted for approval`
      });

      toast.success('COGS card updated and sent for approval');
      setEditMode(false);
      setEditedData(null);
      fetchCard();
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Save edited COGS card without changing status (for approvers)
  const handleSaveOnly = async () => {
    if (!editedData) return;

    setSaving(true);
    try {
      await axios.put(`${API}/cogs-cards/${id}`, {
        ...editedData,
        comments: `Values updated by approver`
      });

      toast.success('Changes saved');
      setEditMode(false);
      setEditedData(null);
      fetchCard();
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Save and approve (for approvers)
  const handleSaveAndApprove = async () => {
    if (!editedData) return;

    setSaving(true);
    try {
      // First save the changes
      await axios.put(`${API}/cogs-cards/${id}`, {
        ...editedData,
        comments: `Values updated and approved`
      });

      // Then approve
      await axios.post(`${API}/cost-cards/${id}/status`, {
        status: 'approved',
        comments: 'Approved with updated values'
      });

      toast.success('Changes saved and approved');
      setEditMode(false);
      setEditedData(null);
      fetchCard();
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save and approve');
    } finally {
      setSaving(false);
    }
  };

  // Cancel edit mode
  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedData(null);
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      await axios.post(`${API}/cost-cards/${id}/comments`, {
        text: newComment
      });
      setNewComment('');
      fetchHistory();
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!newStatus) {
      toast.error('Please select a status');
      return;
    }
    if (!statusComment.trim()) {
      toast.error('Please provide remarks for the status change');
      return;
    }

    setChangingStatus(true);
    try {
      await axios.post(`${API}/cost-cards/${id}/status`, {
        status: newStatus,
        comments: statusComment
      });
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
      setStatusComment('');
      fetchCard();
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to update status`);
    } finally {
      setChangingStatus(false);
    }
  };

  const handleRevise = async () => {
    setChangingStatus(true);
    try {
      await axios.put(`${API}/cost-cards/${id}/revise`, revisionData);
      toast.success('Cost card revised');
      setRevisionMode(false);
      fetchCard();
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Revision failed');
    } finally {
      setChangingStatus(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'approved': return <CheckCircle className="w-3 h-3 text-emerald-400" />;
      case 'rejected': return <XCircle className="w-3 h-3 text-red-400" />;
      case 'submitted':
      case 'resubmitted': return <Send className="w-3 h-3 text-cyan-400" />;
      case 'revised': return <Edit className="w-3 h-3 text-amber-400" />;
      case 'updated': return <Edit className="w-3 h-3 text-blue-400" />;
      case 'comment': return <MessageSquare className="w-3 h-3 text-purple-400" />;
      case 'in_review': return <Eye className="w-3 h-3 text-indigo-400" />;
      case 'on_hold': return <Clock className="w-3 h-3 text-orange-400" />;
      case 'needs_info': return <AlertTriangle className="w-3 h-3 text-yellow-400" />;
      default: return <Clock className="w-3 h-3 text-zinc-600 dark:text-zinc-400" />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'approved': return 'border-l-emerald-500 bg-emerald-500/5';
      case 'rejected': return 'border-l-red-500 bg-red-500/5';
      case 'submitted':
      case 'resubmitted': return 'border-l-cyan-500 bg-cyan-500/5';
      case 'revised': return 'border-l-amber-500 bg-amber-500/5';
      case 'updated': return 'border-l-blue-500 bg-blue-500/5';
      case 'comment': return 'border-l-purple-500 bg-purple-500/5';
      case 'in_review': return 'border-l-indigo-500 bg-indigo-500/5';
      case 'on_hold': return 'border-l-orange-500 bg-orange-500/5';
      case 'needs_info': return 'border-l-yellow-500 bg-yellow-500/5';
      default: return 'border-l-zinc-600 bg-white dark:bg-zinc-900';
    }
  };

  // Check if current user can approve this card
  const isAssignedApprover = card?.assigned_to === user?.id;
  // Check if viewing an old/archived version (not the current active card for this part)
  const currentCardId = versionHistory?.current_card?.id;
  const isOldVersion = card?.is_archived || card?.archived || (currentCardId && currentCardId !== card?.id);

  // const canChangeStatus = (hasRole('admin') || (hasRole('approver') && isAssignedApprover)) && !isOldVersion;
  const canChangeStatus = ((hasRole('admin') || (hasRole('approver')))) && !isOldVersion;

  const canEdit = hasRole('admin', 'creator') && (card?.created_by === user?.id || hasRole('admin')) && !isOldVersion;
  const displayData = viewingSnapshot || card;

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin w-8 h-8 border-2 border-[#C3002F] border-t-transparent rounded-full" />
        </div>
      </MainLayout>
    );
  }

  if (!card) {
    return (
      <MainLayout>
        <div className="p-8 text-center text-zinc-500">Cost card not found</div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex h-[calc(100vh-0px)]" data-testid="cost-card-detail-page">
        {/* Main Content - Left */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/cost-cards')}
              className="text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>

            <div className="flex-1">
              <h1 className="text-2xl font-bold text-zinc-900 dark:text-white flex items-center gap-2">
                <FileText className="w-6 h-6" />
                {viewingSnapshot ? 'Historical Snapshot' : 'COGS Card'}
              </h1>
              <p className="text-sm text-zinc-500">{card.part_number} • Version {card.version || 1}</p>
            </div>

            {/* Status with floating status indicator */}
            <div className="flex items-center gap-3">
              <StatusBadge status={card.status} large />
              {isOldVersion && (
                <span className="px-2 py-1 text-xs bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 rounded border border-amber-300 dark:border-amber-500/30 font-medium">
                  OLD VERSION
                </span>
              )}
            </div>
          </div>

          {/* Old/Archived Version Warning Banner with Details - for old versions navigated from History (hide when viewing Activity snapshot) */}
          {isOldVersion && !viewingSnapshot && (
            <div className="mb-4 p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-lg">
              {/* Warning Header */}
              <div className="flex items-center gap-3 mb-4 pb-3 border-b border-amber-500/20">
                <Archive className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-amber-800 dark:text-amber-300 font-medium">This is an older version</p>
                  <p className="text-amber-700 dark:text-amber-400 text-sm">This card has been superseded by a newer version. It is read-only and cannot be edited.</p>
                </div>
                {versionHistory?.current_card?.id && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/cogs-cards/${versionHistory.current_card.id}`)}
                    className="border-amber-300 dark:border-amber-500/50 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-500/20"
                  >
                    View Current Version
                  </Button>
                )}
              </div>

              {/* Version Details */}
              <div className="flex items-center gap-2 mb-3">
                <History className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 font-medium text-sm uppercase tracking-wider">Version Details</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-1">
                  <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Status</p>
                  <p className={`text-sm font-medium capitalize ${card?.status === 'approved' ? 'text-emerald-400' :
                    card?.status === 'rejected' ? 'text-red-400' :
                      'text-amber-400'
                    }`}>
                    {card?.status?.replace('_', ' ') || 'Unknown'}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Created By</p>
                  <p className="text-sm text-zinc-300">{card?.created_by_name || 'Unknown'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Created On</p>
                  <p className="text-sm text-zinc-300">
                    {card?.created_at ? new Date(card.created_at).toLocaleString() : 'Unknown'}
                  </p>
                </div>
              </div>
              {/* Additional Metadata */}
              <div className="mt-3 pt-3 border-t border-amber-500/20 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-1">
                  <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Part Number</p>
                  <p className="text-sm font-mono text-zinc-300">{card?.part_number || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Part Name</p>
                  <p className="text-sm text-zinc-300">{card?.part_name || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Dealer Price</p>
                  <p className="text-sm font-mono text-emerald-400">₹{card?.dealer_price?.toLocaleString() || '0'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">MRP</p>
                  <p className="text-sm font-mono text-amber-400">₹{card?.mrp?.toLocaleString() || '0'}</p>
                </div>
              </div>
              {/* Approval Info if available */}
              {card?.approved_by_name && (
                <div className="mt-3 pt-3 border-t border-amber-500/20 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Approved By</p>
                    <p className="text-sm text-zinc-300">{card.approved_by_name}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Approved On</p>
                    <p className="text-sm text-zinc-300">
                      {card?.updated_at ? new Date(card.updated_at).toLocaleString() : 'Unknown'}
                    </p>
                  </div>
                  {card?.approver_comments && (
                    <div className="col-span-2 space-y-1">
                      <p className="text-[10px] text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">Approver Remarks</p>
                      <p className="text-sm text-zinc-300 bg-black/20 p-2 rounded">{card.approver_comments}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Historical Snapshot Banner - for snapshots viewed from Activity tab (only show for older snapshots, not the latest) */}
          {viewingSnapshot && viewingSnapshotIndex !== 0 && (
            <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-lg flex items-center gap-3">
              <Archive className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-amber-800 dark:text-amber-300 font-medium">This is an older version</p>
                <p className="text-amber-700 dark:text-amber-400 text-sm">You are viewing a historical snapshot from the activity timeline. It is read-only.</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setViewingSnapshot(null); setViewingSnapshotIndex(null); setViewingSnapshotInfo(null); }}
                className="border-amber-300 dark:border-amber-500/50 text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-500/20"
              >
                View Current Version
              </Button>
            </div>
          )}

          {/* Assigned Approver Info */}
          {card.assigned_to_name && (
            <div className="mb-4 p-3 bg-zinc-100 dark:bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-200 dark:border-zinc-800 rounded flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-zinc-500" />
                <span className="text-zinc-600 dark:text-zinc-400">Assigned to:</span>
                <span className="text-zinc-900 dark:text-white font-medium">{card.assigned_to_name}</span>
              </div>
              <div className="text-xs text-zinc-500">
                Created by {card.created_by_name} • {new Date(card.created_at).toLocaleDateString()}
              </div>
            </div>
          )}

          {/* Status Change Panel - Dropdown Design */}
          {!viewingSnapshot && canChangeStatus && (
            <div className="mb-6 p-4 bg-zinc-100 dark:bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-200 dark:border-zinc-800 rounded-lg">
              <div className="flex items-center gap-4">
                {/* Status Dropdown */}
                <div className="flex-1">
                  <Label className="text-zinc-600 dark:text-zinc-400 text-xs uppercase tracking-wider mb-1.5 block">Update Status</Label>
                  <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                    <SelectTrigger className="bg-white dark:bg-zinc-900 border-zinc-700 h-10">
                      <SelectValue placeholder="Select new status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="approved" className="text-emerald-400" disabled={card.status === 'approved'}>
                        <span className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" /> Approved
                        </span>
                      </SelectItem>
                      <SelectItem value="rejected" className="text-red-400" disabled={card.status === 'rejected'}>
                        <span className="flex items-center gap-2">
                          <XCircle className="w-4 h-4" /> Rejected
                        </span>
                      </SelectItem>
                      <SelectItem value="in_review" disabled={card.status === 'in_review'}>
                        <span className="flex items-center gap-2">
                          <Clock className="w-4 h-4" /> In Review
                        </span>
                      </SelectItem>
                      <SelectItem value="on_hold" disabled={card.status === 'on_hold'}>
                        <span className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" /> On Hold
                        </span>
                      </SelectItem>
                      <SelectItem value="needs_info" disabled={card.status === 'needs_info'}>
                        <span className="flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" /> Needs Info
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Remarks Field */}
                <div className="flex-[2]">
                  <Label className="text-zinc-600 dark:text-zinc-400 text-xs uppercase tracking-wider mb-1.5 block">Remarks *</Label>
                  <Input
                    value={statusComment}
                    onChange={(e) => setStatusComment(e.target.value)}
                    placeholder="Add remarks for this status change..."
                    className="bg-white dark:bg-zinc-900 border-zinc-700 h-10"
                  />
                </div>

                {/* Update Button */}
                <div className="pt-5">
                  <Button
                    onClick={() => handleStatusChange(selectedStatus)}
                    disabled={changingStatus || !statusComment.trim() || !selectedStatus}
                    className={`h-10 px-6 ${selectedStatus === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700' :
                      selectedStatus === 'rejected' ? 'bg-red-600 hover:bg-red-700' :
                        'bg-[#C3002F] hover:bg-[#9f0026]'
                      }`}
                  >
                    {changingStatus ? 'Updating...' : 'Update Status'}
                  </Button>
                </div>
              </div>
              {!statusComment.trim() && (
                <p className="text-xs text-zinc-500 mt-2">Remarks are required for all status changes</p>
              )}
            </div>
          )}

          {/* Rejection Alert */}
          {!viewingSnapshot && card.status === 'rejected' && card.approver_comments && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-red-400 font-medium">Rejection Feedback</p>
                  <p className="text-zinc-700 dark:text-zinc-300 mt-1">{card.approver_comments}</p>
                  <p className="text-xs text-zinc-500 mt-2">by {card.approved_by_name}</p>
                </div>
              </div>

              {/* Revise Button */}
              {canEdit && !revisionMode && (
                <Button
                  onClick={() => setRevisionMode(true)}
                  className="mt-4 w-full bg-amber-600 hover:bg-amber-700"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Revise & Resubmit
                </Button>
              )}
            </div>
          )}

          {/* Approved Success */}
          {!viewingSnapshot && card.status === 'approved' && (
            <div className="mb-6 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-emerald-400 font-medium">Approved</p>
                  {card.approver_comments && (
                    <p className="text-zinc-700 dark:text-zinc-300 mt-1">{card.approver_comments}</p>
                  )}
                  <p className="text-xs text-zinc-500 mt-2">by {card.approved_by_name} on {new Date(card.updated_at).toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          )}

          {/* COGS Card Details - Editable */}
          {!revisionMode && (
            <div className="space-y-4">
              {/* Edit Button - Only show when NOT in edit mode */}
              {!viewingSnapshot && !editMode && canEdit && (
                <div className="flex justify-end">
                  <Button
                    onClick={() => {
                      setEditedData({ ...card });
                      setEditMode(true);
                    }}
                    variant="outline"
                    className="border-zinc-700"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Values
                  </Button>
                </div>
              )}

              <EditableCOGSCard
                key={viewingSnapshotInfo?.id || 'current'}
                data={displayData}
                isHistorical={!!viewingSnapshot}
                isOldVersion={isOldVersion && !viewingSnapshot}
                editMode={editMode}
                editedData={editedData}
                setEditedData={setEditedData}
                onCancel={handleCancelEdit}
                onSave={canChangeStatus ? handleSaveOnly : handleSaveAndResubmit}
                onSaveAndApprove={handleSaveAndApprove}
                saving={saving}
                isApprover={canChangeStatus}
                snapshotInfo={viewingSnapshotInfo}
              />
            </div>
          )}

          {/* Competitor Pricing */}
          <div className="mt-6 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-zinc-900 dark:text-white flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-cyan-500" />
                Competitor Pricing
                {competitorPricing?.total_competitors != null && !competitorLoading && (
                  <span className="text-sm font-normal text-zinc-500">({competitorPricing.total_competitors} competitor{competitorPricing.total_competitors !== 1 ? 's' : ''})</span>
                )}
              </h3>
              {competitorPricing?.our_price != null && !competitorLoading && (
                <span className="text-xs text-zinc-500">Our price: <span className="font-mono font-semibold text-[#C3002F]">₹{Number(competitorPricing.our_price).toLocaleString()}</span></span>
              )}
            </div>
            {competitorLoading ? (
              <div className="flex items-center gap-3 py-6 text-zinc-500">
                <Loader2 className="w-5 h-5 animate-spin text-[#C3002F]" />
                <span className="text-sm">Fetching competitor pricing, this may take a moment...</span>
              </div>
            ) : competitorPricing?.competitors?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/60">
                      <th className="text-left py-2 px-3 text-xs font-medium text-zinc-500 uppercase">Competitor Vehicle</th>
                      <th className="text-left py-2 px-3 text-xs font-medium text-zinc-500 uppercase">Part</th>
                      <th className="text-right py-2 px-3 text-xs font-medium text-zinc-500 uppercase">Competitor Price</th>
                      <th className="text-right py-2 px-3 text-xs font-medium text-zinc-500 uppercase">Difference</th>
                      {/* <th className="text-center py-2 px-3 text-xs font-medium text-zinc-500 uppercase">Confidence</th> */}
                      {/* <th className="py-2 px-3" /> */}
                    </tr>
                  </thead>
                  <tbody>
                    {competitorPricing.competitors.map((c, i) => {
                      const ourPrice = competitorPricing.our_price;
                      const diff = ourPrice != null && c.competitor_price != null ? ourPrice - c.competitor_price : null;
                      const diffPct = diff != null && c.competitor_price ? (diff / c.competitor_price) * 100 : null;
                      return (
                        <tr key={c.competitor_id ?? i} className="border-b border-zinc-100 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-800/50">
                          <td className="py-2 px-3 text-zinc-900 dark:text-white font-medium">
                            {c.competitor_vehicle}
                            {c.competitor_vehicle_type && <span className="ml-1 text-xs text-zinc-400">({c.competitor_vehicle_type})</span>}
                          </td>
                          <td className="py-2 px-3 text-zinc-600 dark:text-zinc-400">{c.competitor_part_name}</td>
                          <td className="py-2 px-3 text-right font-mono text-cyan-600 dark:text-cyan-400">₹{Number(c.competitor_price).toLocaleString()}</td>
                          <td className="py-2 px-3 text-right">
                            {diff != null ? (
                              <span className={`font-mono text-xs font-semibold px-1.5 py-0.5 rounded ${diff >= 0 ? 'bg-red-100 dark:bg-red-500/20 text-red-600 dark:text-red-400' : 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'}`}>
                                {diff >= 0 ? '+' : ''}{Number(diffPct).toFixed(1)}%
                              </span>
                            ) : '—'}
                          </td>
                          {/* <td className="py-2 px-3 text-center">
                            <span className={`text-xs font-mono font-semibold px-1.5 py-0.5 rounded ${c.confidence_score >= 0.8 ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400' : c.confidence_score >= 0.5 ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400' : 'bg-zinc-100 dark:bg-zinc-700 text-zinc-500'}`}>
                              {c.confidence_score != null ? `${Math.round(c.confidence_score * 100)}%` : '—'}
                            </span>
                          </td> */}
                          {/* <td className="py-2 px-3">
                            {c.source_urls?.[0] && (
                              <a href={c.source_urls[0]} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-cyan-600 dark:text-cyan-400 hover:underline whitespace-nowrap">
                                View <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </td> */}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-zinc-500 py-4">No competitor pricing data available for this part.</p>
            )}
          </div>

          {/* Price History */}
          <div className="mt-6 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-5">
            <h3 className="text-base font-semibold text-zinc-900 dark:text-white flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-[#C3002F]" />
              Price History
            </h3>
            {priceHistoryLoading ? (
              <div className="flex items-center gap-3 py-6 text-zinc-500">
                <Loader2 className="w-5 h-5 animate-spin text-[#C3002F]" />
                <span className="text-sm">Loading price history...</span>
              </div>
            ) : priceHistory?.parts?.length > 0 ? (() => {
              const ph = priceHistory.parts[0];
              const phCurrency = ph.price_history[0]?.currency || 'INR';
              const phSymbol = CURRENCY_SYMBOL_MAP[phCurrency] ?? phCurrency;
              const gridColor = isDark ? '#27272a' : '#e4e4e7';
              const axisColor = isDark ? '#52525b' : '#a1a1aa';
              const labelColor = isDark ? '#a1a1aa' : '#52525b';
              const chartData = ph.price_history.map(h => ({
                date: h.date,
                DBP: h.dbp,
                MRP: h.mrp,
                COGS: h.cogs,
              }));
              return (
                <div className="space-y-4">
                  {/* KPI row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg">
                      <p className="text-xs text-zinc-500 uppercase mb-1">First Price</p>
                      <p className="text-lg font-mono font-bold text-zinc-900 dark:text-white">{fmtPrice(ph.first_price, phCurrency)}</p>
                    </div>
                    <div className="p-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg">
                      <p className="text-xs text-zinc-500 uppercase mb-1">Latest Price</p>
                      <p className="text-lg font-mono font-bold text-zinc-900 dark:text-white">{fmtPrice(ph.latest_price, phCurrency)}</p>
                    </div>
                    <div className={`p-4 border rounded-lg ${(ph.price_change ?? 0) >= 0 ? 'bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/20' : 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/20'}`}>
                      <p className={`text-xs uppercase mb-1 ${(ph.price_change ?? 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>Price Change</p>
                      <p className={`text-lg font-mono font-bold ${(ph.price_change ?? 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                        {ph.price_change != null ? `${ph.price_change >= 0 ? '+' : ''}${fmtPrice(ph.price_change, phCurrency)} (${fmtPct(ph.price_change_percent, 2)})` : '-'}
                      </p>
                    </div>
                    <div className="p-4 bg-cyan-50 dark:bg-cyan-500/10 border border-cyan-200 dark:border-cyan-500/20 rounded-lg">
                      <p className="text-xs text-cyan-600 dark:text-cyan-400 uppercase mb-1">Avg GP%</p>
                      <p className="text-lg font-mono font-bold text-cyan-600 dark:text-cyan-400">{fmtPct(ph.avg_gp_percent)}</p>
                    </div>
                  </div>

                  {/* Chart */}
                  <div className="border border-zinc-200 dark:border-zinc-800 p-4 rounded-lg">
                    <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-3">
                      {ph.part_name}
                      <span className="text-xs font-normal text-zinc-500 ml-2">{ph.total_versions} version{ph.total_versions !== 1 ? 's' : ''}</span>
                    </p>
                    <ResponsiveContainer width="100%" height={260}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                        <XAxis dataKey="date" stroke={axisColor} tick={{ fontSize: 10, fill: labelColor }} />
                        <YAxis stroke={axisColor} tickFormatter={(v) => `${phSymbol}${(v / 1000).toFixed(0)}k`} />
                        <Tooltip content={<PriceHistoryTooltip isDark={isDark} currency={phCurrency} />} />
                        <Legend />
                        <Line type="monotone" dataKey="DBP" stroke="#C3002F" strokeWidth={2} dot={{ r: 4 }} />
                        <Line type="monotone" dataKey="MRP" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} />
                        <Line type="monotone" dataKey="COGS" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Version History table */}
                  <div className="border border-zinc-200 dark:border-zinc-800 rounded-lg overflow-hidden">
                    <div className="px-4 py-3 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900">
                      <p className="text-sm font-semibold text-zinc-900 dark:text-white">Version History</p>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900">
                            <th className="text-left py-2 px-4 text-xs font-medium text-zinc-500 uppercase">Version</th>
                            <th className="text-left py-2 px-4 text-xs font-medium text-zinc-500 uppercase">Date</th>
                            <th className="text-left py-2 px-4 text-xs font-medium text-zinc-500 uppercase">Currency</th>
                            <th className="text-left py-2 px-4 text-xs font-medium text-zinc-500 uppercase">DBP</th>
                            <th className="text-left py-2 px-4 text-xs font-medium text-zinc-500 uppercase">MRP</th>
                            <th className="text-left py-2 px-4 text-xs font-medium text-zinc-500 uppercase">COGS</th>
                            <th className="text-left py-2 px-4 text-xs font-medium text-zinc-500 uppercase">GP%</th>
                            <th className="text-left py-2 px-4 text-xs font-medium text-zinc-500 uppercase">Approved By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {ph.price_history.map((h, i) => (
                            <tr key={i} className="border-b border-zinc-200 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-900/50">
                              <td className="py-2 px-4 font-mono text-zinc-500">v{h.version}</td>
                              <td className="py-2 px-4 text-zinc-700 dark:text-zinc-300">{h.date}</td>
                              <td className="py-2 px-4 font-mono text-zinc-500">{h.currency ?? '—'}</td>
                              <td className="py-2 px-4 font-mono text-[#C3002F]">{fmtPrice(h.dbp, h.currency)}</td>
                              <td className="py-2 px-4 font-mono text-amber-600 dark:text-amber-400">{fmtPrice(h.mrp, h.currency)}</td>
                              <td className="py-2 px-4 font-mono text-purple-600 dark:text-purple-400">{fmtPrice(h.cogs, h.currency)}</td>
                              <td className="py-2 px-4 font-mono text-emerald-600 dark:text-emerald-400">{fmtPct(h.gp_percent)}</td>
                              <td className="py-2 px-4 text-zinc-600 dark:text-zinc-400">{h.approved_by ?? '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              );
            })() : (
              <p className="text-sm text-zinc-500 py-4">No approved price history available for this part.</p>
            )}
          </div>
        </div>

        {/* Activity Sidebar - Right */}
        <div className="w-96 bg-white dark:bg-zinc-900 border-l border-zinc-200 dark:border-zinc-800 flex flex-col">
          {/* Sidebar Tabs */}
          <div className="flex border-b border-zinc-200 dark:border-zinc-800">
            <button
              onClick={() => setSidebarTab('activity')}
              className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${sidebarTab === 'activity'
                ? 'text-[#C3002F] border-b-2 border-[#C3002F] bg-zinc-50 dark:bg-zinc-900'
                : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300'
                }`}
            >
              <MessageSquare className="w-4 h-4" />
              Activity
            </button>
            <button
              onClick={() => setSidebarTab('versions')}
              className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${sidebarTab === 'versions'
                ? 'text-[#C3002F] border-b-2 border-[#C3002F] bg-zinc-50 dark:bg-zinc-900'
                : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300'
                }`}
            >
              <History className="w-4 h-4" />
              History
              {versionHistory?.all_versions?.filter(v => v.status === 'approved').length > 0 && (
                <span className="bg-zinc-200 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 text-xs px-1.5 py-0.5 rounded-full">
                  {versionHistory.all_versions.filter(v => v.status === 'approved').length}
                </span>
              )}
            </button>
          </div>

          {/* Activity Tab Content */}
          {sidebarTab === 'activity' && (
            <>
              {/* Comment Input - Always visible at top */}
              <div className="p-4 border-b border-zinc-200 dark:border-zinc-800">
                <form onSubmit={handleAddComment} className="space-y-2">
                  <MentionTextarea
                    value={newComment}
                    onChange={(text) => setNewComment(text)}
                    placeholder="Add a comment... Type @ to mention"
                    className="bg-white dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 h-16 text-sm resize-none"
                    onSubmit={handleAddComment}
                  />
                  <Button
                    type="submit"
                    disabled={submittingComment || !newComment.trim()}
                    size="sm"
                    className="w-full bg-[#C3002F] hover:bg-[#9f0026]"
                  >
                    <Send className="w-3 h-3 mr-2" />
                    {submittingComment ? 'Posting...' : 'Post Comment'}
                  </Button>
                </form>
              </div>

              {/* Activity Filters */}
              <div className="px-3 py-2 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900">
                <div className="flex items-center gap-2 flex-wrap">
                  <Filter className="w-3.5 h-3.5 text-zinc-500" />
                  <Select value={activityStatusFilter} onValueChange={setActivityStatusFilter}>
                    <SelectTrigger className="h-7 text-xs bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 w-[100px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="in_review">In Review</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="on_hold">On Hold</SelectItem>
                      <SelectItem value="needs_info">Needs Info</SelectItem>
                      <SelectItem value="revised">Revised</SelectItem>
                      <SelectItem value="updated">Updated</SelectItem>
                      <SelectItem value="comment">Comments</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={activityDateFilter} onValueChange={(val) => { setActivityDateFilter(val); if (val !== 'exact') setExactDate(''); }}>
                    <SelectTrigger className="h-7 text-xs bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 w-[100px]">
                      <SelectValue placeholder="Date" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Time</SelectItem>
                      <SelectItem value="today">Today</SelectItem>
                      <SelectItem value="week">This Week</SelectItem>
                      <SelectItem value="month">This Month</SelectItem>
                      <SelectItem value="exact">Exact Date</SelectItem>
                    </SelectContent>
                  </Select>
                  {activityDateFilter === 'exact' && (
                    <input
                      type="date"
                      value={exactDate}
                      onChange={(e) => setExactDate(e.target.value)}
                      className="h-7 text-xs bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded px-2 text-zinc-700 dark:text-zinc-300"
                    />
                  )}
                  {(activityStatusFilter !== 'all' || activityDateFilter !== 'all') && (
                    <button
                      onClick={() => { setActivityStatusFilter('all'); setActivityDateFilter('all'); setExactDate(''); }}
                      className="text-xs text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 ml-auto"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              {/* Activity List */}
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                {(() => {
                  // Filter history based on status and date
                  const filteredHistory = history.filter(item => {
                    // Status filter
                    if (activityStatusFilter !== 'all' && item.action !== activityStatusFilter) {
                      return false;
                    }
                    // Date filter
                    if (activityDateFilter !== 'all') {
                      const itemDate = new Date(item.created_at);
                      const now = new Date();
                      if (activityDateFilter === 'today') {
                        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                        if (itemDate < today) return false;
                      } else if (activityDateFilter === 'week') {
                        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                        if (itemDate < weekAgo) return false;
                      } else if (activityDateFilter === 'month') {
                        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                        if (itemDate < monthAgo) return false;
                      } else if (activityDateFilter === 'exact' && exactDate) {
                        const selectedDate = new Date(exactDate);
                        const itemDateOnly = new Date(itemDate.getFullYear(), itemDate.getMonth(), itemDate.getDate());
                        const selectedDateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
                        if (itemDateOnly.getTime() !== selectedDateOnly.getTime()) return false;
                      }
                    }
                    return true;
                  });

                  return filteredHistory.length > 0 ? (
                    filteredHistory.map((item) => {
                      // Find original index in unfiltered history array
                      const originalIndex = history.findIndex(h => h.id === item.id);
                      const isSelected = viewingSnapshotInfo?.id === item.id;
                      return (
                        <div
                          key={item.id}
                          onClick={() => {
                            if (item.snapshot) {
                              // Always update all snapshot states to ensure fresh data
                              setViewingSnapshot(item.snapshot);
                              setViewingSnapshotIndex(originalIndex);
                              setViewingSnapshotInfo({ ...item }); // Clone to ensure new reference
                            }
                          }}
                          className={`p-3 rounded border-l-2 cursor-pointer transition-all hover:bg-zinc-100 dark:bg-zinc-800/50 ${getActionColor(item.action)} ${isSelected ? 'ring-1 ring-[#C3002F]' : ''
                            }`}
                        >
                          <div className="flex items-start gap-2">
                            <div className="mt-0.5">
                              {getActionIcon(item.action)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-xs font-medium capitalize ${item.action === 'approved' ? 'text-emerald-400' :
                                  item.action === 'rejected' ? 'text-red-400' :
                                    item.action === 'revised' ? 'text-amber-400' :
                                      item.action === 'updated' ? 'text-blue-400' :
                                        item.action === 'comment' ? 'text-purple-400' :
                                          item.action === 'in_review' ? 'text-indigo-400' :
                                            item.action === 'on_hold' ? 'text-orange-400' :
                                              item.action === 'needs_info' ? 'text-yellow-400' :
                                                'text-cyan-400'
                                  }`}>
                                  {item.action?.replace('_', ' ')}
                                </span>
                                <span className="text-xs text-zinc-500">•</span>
                                <span className="text-xs text-zinc-600 dark:text-zinc-400">{item.performed_by_name}</span>
                              </div>
                              <p className="text-xs text-zinc-600 mt-1">
                                {new Date(item.created_at).toLocaleString()}
                              </p>
                              {item.comments && (
                                <p className="text-sm text-zinc-700 dark:text-zinc-300 mt-2 p-2 bg-black/20 rounded">
                                  {renderMentionText(item.comments)}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="text-center py-8 text-zinc-500 text-sm">
                      <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      {activityStatusFilter !== 'all' || activityDateFilter !== 'all'
                        ? 'No matching activity'
                        : 'No activity yet'}
                    </div>
                  );
                })()}
                <div ref={commentsEndRef} />
              </div>
            </>
          )}

          {/* Version History Tab Content */}
          {sidebarTab === 'versions' && (
            <div className="flex-1 overflow-y-auto p-4">
              {loadingVersions ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin w-6 h-6 border-2 border-[#C3002F] border-t-transparent rounded-full" />
                </div>
              ) : (() => {
                // Filter to only show approved cards (not rejected)
                const historyCards = versionHistory?.all_versions?.filter(
                  v => v.status === 'approved'
                ) || [];

                return historyCards.length > 0 ? (
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-4 top-6 bottom-6 w-0.5 bg-zinc-200 dark:bg-zinc-700" />

                    <div className="space-y-4">
                      {historyCards.map((version, index) => {
                        const isCurrentCard = version.id === card?.id;

                        return (
                          <div
                            key={version.id}
                            className={`relative pl-10 ${isCurrentCard ? 'opacity-100' : 'opacity-90 hover:opacity-100'}`}
                          >
                            {/* Timeline node */}
                            <div className={`absolute left-2 w-5 h-5 rounded-full border-2 flex items-center justify-center ${isCurrentCard
                              ? 'bg-blue-500 border-blue-500'
                              : 'bg-emerald-500 border-emerald-500'
                              }`}>
                              {isCurrentCard ? (
                                <Eye className="w-2.5 h-2.5 text-white" />
                              ) : (
                                <Check className="w-2.5 h-2.5 text-white" />
                              )}
                            </div>

                            {/* Version card - Clickable to view, but card will be read-only */}
                            <div
                              onClick={() => {
                                // Clear any active Activity snapshot first
                                setViewingSnapshot(null);
                                setViewingSnapshotIndex(null);
                                setViewingSnapshotInfo(null);
                                // Navigate to different version, or stay on current
                                if (!isCurrentCard) {
                                  navigate(`/cogs-cards/${version.id}`);
                                }
                              }}
                              className={`p-3 rounded-lg border transition-all ${isCurrentCard
                                ? 'bg-blue-500/10 border-blue-500/30 cursor-pointer ring-2 ring-blue-500/50'
                                : 'bg-white dark:bg-zinc-800/50 border-zinc-200 dark:border-zinc-700 hover:border-blue-400 dark:hover:border-blue-500 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-500/5'
                                }`}
                            >
                              {/* Version header */}
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  {isCurrentCard ? (
                                    <span className="text-[10px] px-1.5 py-0.5 bg-blue-500 text-white rounded font-medium">
                                      OBSERVING THIS
                                    </span>
                                  ) : (
                                    <span className="text-[10px] px-1.5 py-0.5 bg-zinc-200 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 rounded">
                                      VIEW VERSION
                                    </span>
                                  )}
                                </div>
                                <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400">
                                  APPROVED
                                </span>
                              </div>

                              {/* Pricing info */}
                              <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                                <div>
                                  <span className="text-zinc-500">Dealer:</span>
                                  <span className="ml-1 font-mono text-emerald-600 dark:text-emerald-400">
                                    ₹{version.dealer_price?.toLocaleString()}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-zinc-500">MRP:</span>
                                  <span className="ml-1 font-mono text-amber-600 dark:text-amber-400">
                                    ₹{version.mrp?.toLocaleString()}
                                  </span>
                                </div>
                              </div>

                              {/* Meta info */}
                              <div className="flex items-center gap-2 text-[10px] text-zinc-500">
                                <User className="w-3 h-3" />
                                <span>{version.created_by_name}</span>
                                <span>•</span>
                                <Clock className="w-3 h-3" />
                                <span>{new Date(version.created_at).toLocaleDateString()}</span>
                              </div>

                              {/* Approval info */}
                              {version.approved_by_name && (
                                <div className="mt-2 pt-2 border-t border-zinc-100 dark:border-zinc-700 text-[10px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" />
                                  Approved by {version.approved_by_name}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-zinc-500 text-sm">
                    <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    No approval history
                    <p className="text-xs mt-1">Approved cards will appear here</p>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
